/**
 * Memo de respuestas HTTP en memoria (por pestaña, vive lo que vive la app).
 *
 * Etapa 0 del plan de caché del reporte (docs/DECISIONES.md §19): los tabs del reporte
 * financiero se destruyen y re-fetchean al cambiar de tab (sin keep-alive);
 * con el memo, volver a un tab o alternar un filtro ya visto no vuelve a
 * pegarle al backend dentro de la ventana `ttlMs`.
 *
 * Reglas:
 *  - Sólo guarda respuestas exitosas; la clave la arma el caller (URL +
 *    empresa activa).
 *  - LRU acotado (`maxEntries`) + TTL: la entrada más vieja sale primero.
 *  - Todo memo creado queda registrado para `clearAllResponseMemos()`, que
 *    api.js invoca tras cualquier request de escritura (POST/PUT/PATCH/DELETE):
 *    un cambio hecho desde este navegador nunca muestra un reporte viejo. Los
 *    cambios ajenos (otro usuario, sync SII) los cubre el TTL corto y el cache
 *    generacional del backend.
 *
 * Helper puro: sin reactividad ni imports del store.
 */

const registry = new Set();

export function createResponseMemo({ maxEntries = 50, ttlMs = 5 * 60 * 1000 } = {}) {
    const entries = new Map();

    const memo = {
        get(key) {
            const entry = entries.get(key);
            if (!entry) return undefined;
            if (Date.now() - entry.storedAt > ttlMs) {
                entries.delete(key);
                return undefined;
            }
            // Refresca el orden LRU: Map itera por inserción.
            entries.delete(key);
            entries.set(key, entry);
            return entry.value;
        },
        set(key, value) {
            entries.delete(key);
            entries.set(key, { value, storedAt: Date.now() });
            while (entries.size > maxEntries) {
                entries.delete(entries.keys().next().value);
            }
        },
        clear() {
            entries.clear();
        },
        get size() {
            return entries.size;
        },
    };

    registry.add(memo);
    return memo;
}

export function clearAllResponseMemos() {
    registry.forEach((memo) => memo.clear());
}
